import { levelAt, teleportCommand, type Level, type MapId } from "@69ly/maps";
import type { Combo } from "@69ly/shared";
import { useState, type KeyboardEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { comboStatus } from "../lib/filters.ts";
import { ClipPlayer } from "./ClipPlayer.tsx";
import { CopyButton } from "./CopyButton.tsx";
import { Icon } from "./Icon.tsx";
import { ImportanceTag } from "./Importance.tsx";
import { GrenadeIcon, GrenadeMark, MarkerIcon } from "./MarkerIcon.tsx";
import { OnMap, ThrowLine } from "./Radar.tsx";
import { StatusTag } from "./StatusControl.tsx";
import { Technique } from "./Technique.tsx";
import { StuffLink } from "./StuffDrawer.tsx";
import { clipPoster, MediaViewer, StageMedia, youtubeEmbed } from "./StuffMedia.tsx";
import { WindowsBar } from "./WindowsBar.tsx";

/** Throw spots closer than this (world units) are one spot. */
const SAME_SPOT = 24;

/** Where a combo is thrown from: its first throw's spot. */
export const comboSpot = (c: Combo) => c.steps[0]?.stuff.throw ?? null;

/** What a combo shows: its own video, or one of its throws (by index). */
export type ComboShow = "video" | number;

/** A combo opens on its own video when it has one, else on its first throw. */
export const comboHasVideo = (c: Combo) => Boolean(c.clip || c.youtube);
export const defaultComboShow = (c: Combo): ComboShow => (comboHasVideo(c) ? "video" : 0);

/**
 * Where to stand: the combo's one spot when every throw shares it, else the spot
 * of the throw being shown (the first while the combo's video is shown).
 */
export function comboStand(combo: Combo, show: ComboShow) {
  const first = comboSpot(combo);
  const one = combo.steps.every((s) => first && Math.hypot(s.stuff.throw.x - first.x, s.stuff.throw.y - first.y) < SAME_SPOT && s.stuff.throw.z === first.z);
  const index = typeof show === "number" ? show : 0;
  return { one, index, spot: one ? first : (combo.steps[index]?.stuff.throw ?? first) };
}

/** The teleport to where to stand, and what it is for. */
export function ComboStand({ combo, show, heading }: { combo: Combo; show: ComboShow; heading: (title: string) => ReactNode }) {
  const { t } = useTranslation();
  const { one, index, spot } = comboStand(combo, show);
  if (!spot) return null;
  return (
    <>
      {heading(one ? t("comboDetail.spot") : t("comboMedia.standFor", { n: index + 1 }))}
      <div className="command">
        <code>{teleportCommand(spot)}</code>
        <CopyButton text={teleportCommand(spot)} />
      </div>
      <p className="muted small">{one ? t("combo.teleportHelp") : t("comboMedia.standHelp")}</p>
    </>
  );
}

/** A combo's marker face: up to three grenade shapes, overlapping, and the count. */
export function ComboGlyphs({ combo, size = 12 }: { combo: Combo; size?: number }) {
  return (
    <span className="combo-glyphs" aria-hidden="true">
      {combo.steps.slice(0, 3).map((s, i) => (
        <GrenadeMark key={i} grenade={s.stuff.grenade} side={combo.side} size={Math.round(size * 2.1)} />
      ))}
      <span className="combo-count">{combo.steps.length}</span>
    </span>
  );
}

/**
 * A selected combo on the radar, drawn like a selected single: each throw's
 * line, its impact icon (numbered) where it lands, the launch icon where it is
 * thrown from. With `onStep`, a landing is a button that shows that throw.
 */
export function ComboOnMap({
  map,
  combo,
  level,
  active = null,
  onStep,
}: {
  map: MapId;
  combo: Combo;
  level: Level;
  /** The throw being shown: its line and landing stand out. */
  active?: number | null;
  onStep?: (index: number) => void;
}) {
  const { t } = useTranslation();
  const onLevel = (z: number | null | undefined) => levelAt(map, z ?? 0) === level;
  // One launch icon per spot; the throw being shown gives it its grenade.
  const spots: { x: number; y: number; z: number; steps: number[] }[] = [];
  combo.steps.forEach((s, i) => {
    const p = s.stuff.throw;
    const near = spots.find((q) => Math.hypot(q.x - p.x, q.y - p.y) < SAME_SPOT && q.z === p.z);
    if (near) near.steps.push(i);
    else spots.push({ x: p.x, y: p.y, z: p.z, steps: [i] });
  });
  const dim = (i: number) => active !== null && active !== i;
  return (
    <>
      {combo.steps.map((s, i) => (
        <ThrowLine key={`l${i}`} map={map} from={s.stuff.throw} to={s.stuff.landing} side={combo.side} className={dim(i) ? "is-dim" : active === i ? "is-active" : undefined} />
      ))}
      {spots.map((q, j) => {
        if (!onLevel(q.z)) return null;
        const shown = q.steps.includes(active ?? -1) ? (active as number) : q.steps[0];
        const stuff = combo.steps[shown].stuff;
        return (
          <OnMap key={`s${j}`} map={map} x={q.x} y={q.y} className="throw-spot">
            <span className={`throw-marker side-${combo.side}${q.steps.every(dim) ? " is-dim" : ""}`} title={t("mapView.throwFrom")}>
              <MarkerIcon kind="launch" grenade={stuff.grenade} side={combo.side} selected={active !== null && q.steps.includes(active)} />
            </span>
          </OnMap>
        );
      })}
      {combo.steps.map((s, i) => {
        if (!onLevel(s.stuff.landing.z ?? s.stuff.throw.z)) return null;
        const face = (
          <>
            <MarkerIcon kind="impact" grenade={s.stuff.grenade} side={combo.side} selected={active === i} />
            <span className="step-number">{i + 1}</span>
          </>
        );
        return (
          <OnMap key={`p${i}`} map={map} x={s.stuff.landing.x} y={s.stuff.landing.y}>
            {onStep ? (
              <button
                type="button"
                className={`marker step-landing side-${combo.side}${dim(i) ? " is-dim" : ""}`}
                aria-pressed={active === i}
                aria-label={t("comboMedia.landing", { n: i + 1, title: s.stuff.title })}
                title={s.stuff.title}
                onClick={(e) => {
                  e.stopPropagation();
                  onStep(i);
                }}
              >
                {face}
              </button>
            ) : (
              <span className={`marker step-landing is-static side-${combo.side}`} title={s.stuff.title}>
                {face}
              </span>
            )}
          </OnMap>
        );
      })}
    </>
  );
}

/** The combo's own video: its upload, or YouTube (a switch when it has both); on its own and silent with `autoPlay`. */
function ComboVideo({ combo, autoPlay = false }: { combo: Combo; autoPlay?: boolean }) {
  const { t } = useTranslation();
  const [own, setOwn] = useState<"clip" | "youtube">(combo.clip ? "clip" : "youtube");
  const kind = own === "clip" && combo.clip ? "clip" : combo.youtube ? "youtube" : "clip";
  return (
    <div className="combo-video">
      {combo.clip && combo.youtube && (
        <div className="segmented segmented-small" role="group" aria-label={t("media.show")}>
          {(["clip", "youtube"] as const).map((k) => (
            <button key={k} type="button" className="seg" aria-pressed={kind === k} onClick={() => setOwn(k)}>
              <Icon name="play" size={13} />
              {t(`media.${k}`)}
            </button>
          ))}
        </div>
      )}
      {kind === "clip" && combo.clip ? (
        <ClipPlayer src={combo.clip.url} title={combo.title} poster={combo.steps[0] ? clipPoster(combo.steps[0].stuff) : undefined} autoPlay={autoPlay} />
      ) : combo.youtube ? (
        <div className="video">
          <iframe
            src={youtubeEmbed(combo.youtube, autoPlay)}
            title={combo.title}
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
          />
        </div>
      ) : null}
    </div>
  );
}

/** One throw of the combo: what it is, how, its note, and all its pictures and video (video first on a stage). */
function ComboThrow({ combo, index, stage }: { combo: Combo; index: number; stage?: boolean }) {
  const { t } = useTranslation();
  const s = combo.steps[index];
  return (
    <div className="combo-throw">
      <p className="combo-throw-head">
        <span className="step-number">{index + 1}</span>
        <GrenadeIcon grenade={s.stuff.grenade} side={combo.side} size={22} />
        <StuffLink id={s.stuff.id} className="combo-throw-title">
          {s.stuff.title}
        </StuffLink>
      </p>
      <Technique technique={s.stuff.technique} compact />
      {s.note && <p className="step-note small">{s.note}</p>}
      {stage ? <StageMedia key={s.stuff.id} stuff={s.stuff} empty={t("comboMedia.noMedia")} /> : <MediaViewer key={s.stuff.id} stuff={s.stuff} empty={t("comboMedia.noMedia")} />}
    </div>
  );
}

/**
 * A combo's video and each throw's pictures and video, one at a time: a tab for
 * the whole combo (when it has a video), one per throw, and previous / next to
 * go through the throws in order. Controlled, so the map can follow. With
 * `stage` (Live), videos play by themselves and each throw shows video first.
 */
export function ComboMedia({ combo, show, onShow, compact, stage }: { combo: Combo; show: ComboShow; onShow: (s: ComboShow) => void; compact?: boolean; stage?: boolean }) {
  const { t } = useTranslation();
  const tabs: ComboShow[] = [...(comboHasVideo(combo) ? (["video"] as const) : []), ...combo.steps.map((_, i) => i)];
  const current: ComboShow = tabs.includes(show) ? show : tabs[0];
  if (current === undefined) return null;
  const at = tabs.indexOf(current);
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const step = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
    if (!step) return;
    e.preventDefault();
    const next = tabs[(at + step + tabs.length) % tabs.length];
    onShow(next);
    e.currentTarget.querySelector<HTMLButtonElement>(`[data-tab="${next}"]`)?.focus();
  };
  const i = typeof current === "number" ? current : null;
  return (
    <section className={`combo-media${compact ? " is-compact" : ""}`} aria-label={t("comboMedia.label")}>
      <div className="combo-tabs" role="tablist" aria-label={t("comboMedia.label")} onKeyDown={onKey}>
        {tabs.map((k) => {
          const selected = k === current;
          if (k === "video")
            return (
              <button key="video" type="button" role="tab" data-tab="video" className="combo-tab" aria-selected={selected} tabIndex={selected ? 0 : -1} onClick={() => onShow("video")}>
                <Icon name="play" size={13} />
                {compact ? t("comboMedia.wholeShort") : t("comboMedia.whole")}
              </button>
            );
          const s = combo.steps[k].stuff;
          return (
            <button
              key={k}
              type="button"
              role="tab"
              data-tab={k}
              className="combo-tab"
              aria-selected={selected}
              tabIndex={selected ? 0 : -1}
              title={s.title}
              aria-label={t("comboMedia.throwN", { n: k + 1, title: s.title })}
              onClick={() => onShow(k)}
            >
              <span className="step-number small-step">{k + 1}</span>
              <GrenadeIcon grenade={s.grenade} side={combo.side} size={18} />
              {!compact && <span className="combo-media-step-title">{s.title}</span>}
            </button>
          );
        })}
      </div>
      <div role="tabpanel" className="combo-media-body">
        {i === null ? <ComboVideo combo={combo} autoPlay={stage} /> : <ComboThrow combo={combo} index={i} stage={stage} />}
      </div>
      {combo.steps.length > 1 && (
        <div className="combo-media-nav">
          <button type="button" className="btn btn-quiet btn-small" disabled={at <= 0} onClick={() => onShow(tabs[at - 1])}>
            <Icon name="chevronLeft" size={14} />
            {t("comboMedia.prev")}
          </button>
          <span className="muted small">{i === null ? t("comboMedia.wholeOf", { count: combo.steps.length }) : t("comboMedia.of", { n: i + 1, count: combo.steps.length })}</span>
          <button type="button" className="btn btn-quiet btn-small" disabled={at >= tabs.length - 1} onClick={() => onShow(tabs[at + 1])}>
            {t("comboMedia.next")}
            <Icon name="chevronRight" size={14} />
          </button>
        </div>
      )}
    </section>
  );
}

/** The ordered throws of a combo, with notes. With `onPick`, a row shows that throw. */
export function ComboSteps({ combo, linked = true, active = null, onPick }: { combo: Combo; linked?: boolean; active?: number | null; onPick?: (index: number) => void }) {
  const { t } = useTranslation();
  return (
    <ol className="combo-steps">
      {combo.steps.map((s, i) => (
        <li key={s.stuff.id} className={active === i ? "is-active" : undefined}>
          {onPick ? (
            <>
              <button type="button" className="combo-step-pick" aria-pressed={active === i} onClick={() => onPick(i)}>
                <span className="step-number">{i + 1}</span>
                <GrenadeIcon grenade={s.stuff.grenade} side={combo.side} size={22} />
                <span className="combo-step-text">
                  {s.stuff.title}
                  {s.note && <span className="muted small"> · {s.note}</span>}
                </span>
              </button>
              <StuffLink id={s.stuff.id} className="plan-line-link">
                <span className="visually-hidden">{t("plan.openUtility", { title: s.stuff.title })}</span>
                <Icon name="arrow" size={11} />
              </StuffLink>
            </>
          ) : (
            <>
              <span className="step-number">{i + 1}</span>
              <GrenadeIcon grenade={s.stuff.grenade} side={combo.side} size={22} />
              <span className="combo-step-text">
                {linked ? <StuffLink id={s.stuff.id}>{s.stuff.title}</StuffLink> : s.stuff.title}
                {s.note && <span className="muted small"> · {s.note}</span>}
              </span>
            </>
          )}
          <StatusTag status={s.stuff.myStatus} />
        </li>
      ))}
      {combo.steps.length < 2 && <li className="muted small">{t("combo.tooShort")}</li>}
    </ol>
  );
}

/** What the side panel shows for a selected combo: its video and each throw's media first. */
export function ComboPanel({ combo, show, onShow }: { combo: Combo; show: ComboShow; onShow: (s: ComboShow) => void }) {
  const { t } = useTranslation();
  const spot = comboSpot(combo);
  const status = comboStatus(combo);
  return (
    <div className="stuff-panel">
      <div className="stuff-panel-head">
        <ComboGlyphs combo={combo} size={14} />
        <div>
          <h2 className="panel-title">{combo.title}</h2>
          <p className="muted small">
            <span className={`side-tag side-${combo.side}`}>{t(`sideShort.${combo.side}`)}</span> <ImportanceTag importance={combo.importance} /> {t("combo.kind")} ·{" "}
            {t(`area.${combo.area}`)} · {t("combo.throws", { count: combo.steps.length })}
          </p>
        </div>
      </div>
      <div className="row">
        <Link to={`/combos/${combo.id}`} className="btn btn-secondary btn-small">
          <Icon name="external" size={14} />
          {t("sheet.fullPage")}
        </Link>
        {combo.canEdit && (
          <Link to={`/combos/${combo.id}/edit`} className="btn btn-quiet btn-small">
            {t("mapView.edit")}
          </Link>
        )}
      </div>
      <ComboMedia combo={combo} show={show} onShow={onShow} compact />
      <section>
        <h3 className="eyebrow">{t("combo.order")}</h3>
        <ComboSteps combo={combo} active={typeof show === "number" ? show : null} onPick={onShow} />
      </section>
      <section>
        <h3 className="eyebrow">{t("mapView.windows")}</h3>
        <WindowsBar windows={combo.windows} />
      </section>
      {spot && (
        <section>
          <ComboStand combo={combo} show={show} heading={(title) => <h3 className="eyebrow">{title}</h3>} />
        </section>
      )}
      <section>
        <h3 className="eyebrow">{t("mapView.myStatus")}</h3>
        <p className="row">
          <StatusTag status={status} />
          <span className="muted small">
            {t("combo.learnedOf", { learned: combo.learnedByMe, total: combo.steps.length })} · {t("mapView.by", { name: combo.author.name })}
          </span>
        </p>
      </section>
    </div>
  );
}
